import axios from "axios";
import { notifyChange } from "./liveRefresh";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const api = axios.create({
  baseURL: API,
  timeout: 25000, // 25s — fail fast, not 60s
});

// Attach bearer token — read once per request, no async overhead
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("lss_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Announce writes + retry once on 5xx/network error with short delay
// (Hostinger server recovery).
//
// The success handler is what makes every screen self-updating: the moment a
// POST/PUT/PATCH/DELETE lands, all mounted screens — in this tab and in every
// other tab — refetch. GET is excluded on purpose, because a refetch is
// itself a GET: announcing reads would give the app an infinite loop, while
// announcing only writes terminates after one round.
api.interceptors.response.use(
  (res) => {
    const method = String(res.config?.method || "get").toLowerCase();
    if (method !== "get" && method !== "head") {
      try {
        notifyChange();
      } catch (_) {
        /* an announcement must never be able to fail a successful write */
      }
    }
    return res;
  },
  async (err) => {
    const cfg = err.config;
    if (!cfg || cfg._retry) return Promise.reject(err);
    if (!err.response || err.response.status >= 500) {
      cfg._retry = true;
      await new Promise((r) => setTimeout(r, 800));
      return api(cfg);
    }
    return Promise.reject(err);
  }
);

export default api;

// Keep-alive ping for backend service on app start
let _pinged = false;
export function pingBackend() {
  if (_pinged) return;
  _pinged = true;
  fetch(`${API}/health`, { method: "GET" }).catch(() => {});
}

export const money = (n, decimals) => {
  if (n == null || isNaN(n)) return "₹0";
  const num = Number(n);
  const maxDec = decimals !== undefined ? decimals : (Number.isInteger(num) ? 0 : 2);
  return "₹" + num.toLocaleString("en-IN", { minimumFractionDigits: maxDec > 0 ? 2 : 0, maximumFractionDigits: maxDec });
};

export const moneyFull = (n) => {
  if (n == null || isNaN(n)) return "₹0.00";
  return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
};

// Fetch a file through the authenticated axios client and hand it to the
// browser as a download. A plain <a href="/api/..."> would depend on the
// access_token cookie surviving the navigation; this sends the same Bearer
// token every other request uses, so it works wherever the API works.
export async function downloadFile(url, filename) {
  try {
    const res = await api.get(url, { responseType: "blob", timeout: 60000 });
    const objectUrl = URL.createObjectURL(new Blob([res.data]));
    try {
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      // Deferred: revoking synchronously can cancel the download outright in
      // some browsers, which looks like "the button does nothing".
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    }
    return filename;
  } catch (e) {
    throw new Error(await blobDetail(e) || errMsg(e));
  }
}

// With responseType "blob" an error body arrives as a Blob, so errMsg()'s
// `e.response.data.detail` reads an object it cannot parse. Pull the text back
// out first; return "" when there is nothing useful in there.
async function blobDetail(e) {
  const data = e?.response?.data;
  if (!(data instanceof Blob)) return "";
  try {
    const parsed = JSON.parse(await data.text());
    const d = parsed?.detail;
    if (!d) return "";
    return typeof d === "string" ? d : JSON.stringify(d);
  } catch {
    return "";
  }
}

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (e?.code === "ECONNABORTED" || e?.message?.includes("timeout")) {
    return "Server is warming up, retrying...";
  }
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(", ");
  return JSON.stringify(d);
};
